/**
 * Field Observation tool — the IN-APP full-screen host (QC/sketch/site-analysis family chrome).
 * Launched from a milestone's Documents ⋯ menu (a FIELD_OBSERVATION_MS_TYPES row in
 * milestoneDocumentProviders.js) via the `open-field-observation-modal` CustomEvent
 * ({ msId, projectId, projectName, msName }).
 *
 * Since Phase C the capture UI itself lives in FieldObservationCapture.jsx behind an `io`
 * adapter — this host passes the ONLINE adapter (straight service calls, Phase B behavior
 * unchanged) and adds a "Field mode" link that opens the same gate on the standalone offline-
 * capable /field page (docs/FIELD_OBSERVATIONS.md, Phase C section).
 */
import { useState, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import FieldObservationCapture, { buildOnlineIo } from './FieldObservationCapture.jsx'

export default function FieldObservationHost() {
  const [launch, setLaunch] = useState(null)
  useEffect(() => {
    const h = e => setLaunch(e.detail || null)
    window.addEventListener('open-field-observation-modal', h)
    return () => window.removeEventListener('open-field-observation-modal', h)
  }, [])
  if (!launch) return null
  return <FieldObservationModal key={launch.msId} {...launch} onClose={() => setLaunch(null)} />
}

function FieldObservationModal({ msId, projectId, projectName, msName, onClose }) {
  const [observation, setObservation] = useState(null)
  const memberId = wf.currentMemberId || null
  const byName = ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Full Name'] || ''

  const io = useMemo(() => buildOnlineIo({ msId, projectId, memberId, byName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [msId, projectId])

  useEffect(() => { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = '' } }, [])

  const locked = observation && observation.status === 'issued'
  const fieldModeUrl = '/field?msId=' + encodeURIComponent(msId)
    + (projectId ? '&projectId=' + encodeURIComponent(projectId) : '')
    + (msName ? '&msName=' + encodeURIComponent(msName) : '')
    + (projectName ? '&projectName=' + encodeURIComponent(projectName) : '')

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'var(--aj-bg-page)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ background: '#07272D', color: '#fff', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: '"Montserrat", sans-serif', fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', opacity: 0.7 }}>Field Observation</div>
          <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 16, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {msName || 'Milestone'}{projectName ? ' — ' + projectName : ''}
          </div>
        </div>
        <a href={fieldModeUrl} target="_blank" rel="noopener noreferrer" title="Open AlderFIELD (works offline on site)" style={{
          fontFamily: '"Montserrat", sans-serif', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
          padding: '4px 10px', borderRadius: 20, border: '0.5px solid rgba(255,255,255,0.4)', color: '#fff', textDecoration: 'none',
        }}>Open AlderFIELD</a>
        {observation && (
          <span style={{
            fontFamily: '"Montserrat", sans-serif', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 20, background: locked ? 'rgba(29,158,117,0.25)' : 'rgba(255,255,255,0.15)', color: locked ? '#6fe3bd' : '#fff',
          }}>{locked ? 'Issued' : 'Draft'}</span>
        )}
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: 4 }}>&#10005;</button>
      </div>
      <FieldObservationCapture io={io} onObservation={setObservation} />
    </div>,
    document.body
  )
}
